import { useMemo, useState } from 'react';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { autoDetectMapping, isMappingComplete } from '@shipswift/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const NONE = '__none__';

const FIELDS = [
  { key: 'fullName', label: 'Full name', hint: 'or use first and last below' },
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'address1', label: 'Street address', required: true },
  { key: 'address2', label: 'Address line 2' },
  { key: 'city', label: 'City', required: true },
  { key: 'state', label: 'State', required: true },
  { key: 'zip', label: 'ZIP code', required: true },
  { key: 'country', label: 'Country' },
];

/**
 * Match spreadsheet columns to shipping fields.
 *
 * Guesses on load using the same patterns the original app used, then shows a
 * live preview of the first row so a wrong guess is visible before it becomes
 * five hundred labels addressed to the wrong column.
 */
export default function ColumnMapper({ headers, previewRows, onComplete, onCancel }) {
  const [mapping, setMapping] = useState(() => autoDetectMapping(headers));

  const complete = isMappingComplete(mapping);

  const preview = useMemo(() => {
    const row = previewRows[0] ?? {};
    const valueOf = (key) => (mapping[key] ? (row[mapping[key]] ?? '').trim() : '');

    const name =
      valueOf('fullName') || [valueOf('firstName'), valueOf('lastName')].filter(Boolean).join(' ');

    return {
      name,
      address1: valueOf('address1'),
      address2: valueOf('address2'),
      city: valueOf('city'),
      state: valueOf('state'),
      zip: valueOf('zip'),
    };
  }, [mapping, previewRows]);

  const setField = (key) => (value) =>
    setMapping((prev) => ({ ...prev, [key]: value === NONE ? undefined : value }));

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardTitle className="flex items-center gap-2">
          <ArrowRight className="w-5 h-5" />
          Match your columns
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        <Alert className="border-blue-200 bg-blue-50">
          <Sparkles className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            {previewRows.length} row{previewRows.length === 1 ? '' : 's'} found. We guessed the
            columns below — check the preview and fix anything wrong.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {FIELDS.map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label htmlFor={`map-${field.key}`}>
                  {field.label}
                  {field.required && <span className="text-red-600"> *</span>}
                  {field.hint && (
                    <span className="text-xs font-normal text-slate-500"> — {field.hint}</span>
                  )}
                </Label>
                <Select value={mapping[field.key] ?? NONE} onValueChange={setField(field.key)}>
                  <SelectTrigger id={`map-${field.key}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— not in this file —</SelectItem>
                    {headers.map((header) => (
                      <SelectItem key={header} value={header}>
                        {header}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2">
              Preview — first row of your file
            </p>
            <div className="rounded-lg border bg-slate-50 p-4 font-mono text-sm space-y-0.5 min-h-[140px]">
              {preview.name ? (
                <>
                  <div className="font-semibold text-slate-900">{preview.name}</div>
                  <div className="text-slate-700">{preview.address1 || '—'}</div>
                  {preview.address2 && <div className="text-slate-700">{preview.address2}</div>}
                  <div className="text-slate-700">
                    {preview.city || '—'}, {preview.state || '—'} {preview.zip || '—'}
                  </div>
                </>
              ) : (
                <p className="text-slate-400 font-sans">
                  Choose a name column to see how a label will read.
                </p>
              )}
            </div>

            {!complete && (
              <p className="text-sm text-amber-700 mt-3">
                A name, street address, city, state, and ZIP are all needed before continuing.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Start over
          </Button>
          <Button
            onClick={() => onComplete(mapping)}
            disabled={!complete}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            Looks right — continue
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
